import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { usePet } from "@/hooks/usePets";
import { useClinicFeatureFlags } from "@/hooks/useClinicFeatureFlags";
import {
  useConsultationTranscription,
  type DiagnosisSuggestion,
  type UnknownTerm,
} from "@/hooks/useConsultationTranscription";
import { captureTranscriptionInfo } from "@/lib/monitoring";
import { useConsultationAudioArchiveRecorder } from "./useConsultationAudioArchiveRecorder";
import { DEFAULT_AUDIO_CONSTRAINTS, getMicrophoneErrorMessage } from "./microphone-access";
import { AI_ANALYZING_STALE_MS, isAiProcessingStale } from "./ai-processing-health";
import { decideAiRetry } from "./ai-retry-policy";
import { resolveHistoryApplySource } from "./history-apply-source";
import { decideHistoryReview } from "./history-review-policy";
import { isTimerRunning, resolveMachineState } from "./machine-state";
import { decideReconnect } from "./reconnect-policy";
import { MAX_AUTO_RECONNECT_ATTEMPTS } from "./constants";
import { loadTranscriptDraft } from "./transcriptDraftStore";
import { chooseTranscriptSource } from "./transcript-recovery";
import type { AiStatus, CaptureStatus } from "./transcription-status";
import { useSonioxRecording } from "./useSonioxRecording";
import { useTranscriptSync } from "./useTranscriptSync";
import { useTranscriptionSession, type ConsultationTranscriptionRow } from "./useTranscriptionSession";
import { ensurePortugueseLabel } from "./utils";
import type { TranscriptionMachineState } from "./types";
import type { MedicalRecordInput } from "@/lib/validations/medical-record";

export type TranscriptionSessionConfig = {
  transcriptionId?: string | null;
  petId: string;
  clinicId?: string | null;
  appointmentId?: string | null;
  medicalRecordId?: string | null;
  petName?: string | null;
  appointmentDate?: string | null;
  disableAutoRecovery?: boolean;
  onApply?: (data: Partial<MedicalRecordInput>, transcript: string, summary?: string | null) => void;
  onMergeFields?: (data: Partial<MedicalRecordInput>) => void;
};

const asStructuredRecord = (value: ConsultationTranscriptionRow["structured"]): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

interface UseConsultationTranscriptionControllerParams {
  isOpen: boolean;
  isMinimized: boolean;
  session: TranscriptionSessionConfig | null;
  setIsOpen: (value: boolean) => void;
  setIsMinimized: (value: boolean) => void;
  setSession: React.Dispatch<React.SetStateAction<TranscriptionSessionConfig | null>>;
}

const isCaptureStatus = (value: string | null | undefined): value is CaptureStatus =>
  value === "draft" || value === "finalized" || value === "error";

export const useConsultationTranscriptionController = ({
  isOpen,
  isMinimized,
  session,
  setIsOpen,
  setIsMinimized,
  setSession,
}: UseConsultationTranscriptionControllerParams) => {
  const { t } = useTranslation("medicalRecords");
  const petId = session?.petId ?? "";
  const clinicId = session?.clinicId ?? null;
  const appointmentId = session?.appointmentId ?? null;
  const medicalRecordId = session?.medicalRecordId ?? null;
  const initialTranscriptionId = session?.transcriptionId ?? null;
  const petName = session?.petName ?? null;
  const appointmentDate = session?.appointmentDate ?? null;
  const onApply = session?.onApply;
  const onMergeFields = session?.onMergeFields;
  const disableAutoRecovery = session?.disableAutoRecovery === true;

  const [machineState, setMachineState] = useState<TranscriptionMachineState>("closed");
  const [captureStatus, setCaptureStatus] = useState<CaptureStatus | null>(null);
  const [aiAttempts, setAiAttempts] = useState(0);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [isCancelRecordingDialogOpen, setIsCancelRecordingDialogOpen] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [showReconnectBanner, setShowReconnectBanner] = useState(false);
  const [finalizeFailed, setFinalizeFailed] = useState(false);

  const metadataRef = useRef<Record<string, unknown>>({});
  const micStreamRef = useRef<MediaStream | null>(null);
  const isHydratingRef = useRef(false);
  const hasAutoAppliedRef = useRef(false);
  const shouldAutoApplyRef = useRef(true);
  const discardedSessionIdRef = useRef<string | null>(null);
  const discardedPetIdRef = useRef<string | null>(null);
  const analyzingPollRef = useRef<number | null>(null);
  const hydrationKeyRef = useRef<string | null>(null);
  const autoRetriedRef = useRef(false);
  const finalizingRef = useRef(false);
  const pendingAiExtractionRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);

  const { data: pet } = usePet(petId);
  const { data: currentProfile } = useQuery({
    queryKey: ["current-profile"],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return null;

      const { data, error } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", userData.user.id)
        .single();

      if (error) return null;
      return data;
    },
    enabled: !!petId,
  });

  const {
    status: aiStatus,
    transcript,
    structured,
    summary,
    diagnosisSuggestions,
    unknownTerms,
    detectedLanguage,
    warning,
    isProcessing,
    error,
    reset,
    loadFromSession,
    runAiExtraction,
    markAiFailed,
  } = useConsultationTranscription();

  const {
    sessionId,
    sessionIdRef,
    isSessionLoading,
    sessionError,
    history,
    isHistoryLoading,
    fetchHistory,
    fetchSessionById,
    loadLatestSession,
    ensureSession,
    resetSession,
  } = useTranscriptionSession({
    initialTranscriptionId,
    petId,
    clinicId,
    appointmentId,
    medicalRecordId,
    metadata: metadataRef.current,
  });

  const audioArchiveRecorder = useConsultationAudioArchiveRecorder({
    clinicId,
    petId,
  });
  const { data: clinicFeatureFlags } = useClinicFeatureFlags();
  const isAudioArchiveEnabled = clinicFeatureFlags?.enableTranscriptionAudioArchive === true;

  /**
   * Congela o transcript e dispara a extracao por IA. Fica fora do hook de captura
   * porque a finalizacao (escrita no banco) e o processamento por IA sao
   * responsabilidades separadas do controller, nao do fornecedor de streaming.
   */
  const handleCaptureFinalized = useCallback(
    async (finalTranscript: string) => {
      const transcriptionId = sessionIdRef.current;
      if (!transcriptionId || !finalTranscript.trim()) {
        setSonioxError(t("transcription.errors.emptyTranscript"));
        setIsFinalizing(false);
        return;
      }

      // O encerramento pode chegar por dois caminhos (onFinished do SDK e watchdog).
      // Sem esta guarda, os dois disparariam extracoes concorrentes e a perdedora
      // do lease voltaria com "already_processing".
      if (finalizingRef.current) {
        console.log("[transcription] finalize:skippedDuplicate", { transcriptionId });
        return;
      }
      finalizingRef.current = true;

      const finalized = await finalizeTranscript(finalTranscript);
      setIsFinalizing(false);

      if (!finalized) {
        // Texto seguro no IndexedDB, mas o banco nao aceitou (tipicamente offline).
        // Libera a guarda para que o usuario possa encerrar de novo, e avisa —
        // ficar em silencio aqui daria a impressao de que o encerramento funcionou.
        finalizingRef.current = false;
        setFinalizeFailed(true);
        return;
      }

      setFinalizeFailed(false);
      setCaptureStatus("finalized");
      autoRetriedRef.current = false;

      // Sem rede a extracao nao tem como funcionar (a IA depende do provedor externo).
      // Tentar assim mesmo so produziria um giro inutil ate o watchdog. Marca como
      // pendente: o listener de `online` dispara assim que a conexao voltar.
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        console.log("[transcription] aiExtraction:deferredOffline", { transcriptionId });
        pendingAiExtractionRef.current = true;
        markAiFailed();
        return;
      }

      await runAiExtraction(transcriptionId, crypto.randomUUID(), { metadata: metadataRef.current });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runAiExtraction, sessionIdRef, t],
  );

  const {
    recordingPhase,
    isStarting,
    isFinalizing,
    isInterrupted,
    sonioxError,
    editableTranscript,
    recordingDuration,
    setEditableTranscript,
    setSonioxError,
    setIsFinalizing,
    setIsInterrupted,
    cleanupSoniox,
    cancelActiveRecording,
    handleStartRecording,
    handleStopRecording,
    handlePauseRecording,
    handleResumeRecording,
  } = useSonioxRecording({
    petId,
    ensureSession,
    reset,
    metadata: metadataRef.current,
    sessionIdRef,
    onFinalized: (finalTranscript) => {
      void handleCaptureFinalized(finalTranscript);
    },
    onConnectionLost: () => {
      setSession((previous) =>
        previous ? { ...previous, transcriptionId: null } : previous,
      );
    },
  });

  const stopAndReleaseRecordingStream = useCallback(() => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
  }, []);

  const { syncStatus, lastSyncedAt, finalizeTranscript } = useTranscriptSync({
    sessionIdRef,
    transcript: editableTranscript,
    isCapturing: recordingPhase === "recording" || recordingPhase === "paused",
  });

  useEffect(() => {
    metadataRef.current = {
      pet_name: petName,
      appointment_date: appointmentDate,
      pet_id: petId,
      clinic_id: clinicId,
      appointment_id: appointmentId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      vet_name: currentProfile?.full_name ?? null,
      owner_name: pet?.owner?.name ?? null,
    };
  }, [appointmentDate, appointmentId, clinicId, currentProfile?.full_name, pet?.owner?.name, petId, petName]);

  useEffect(() => {
    setMachineState(
      resolveMachineState({
        isOpen,
        hasSession: Boolean(session),
        recordingPhase,
        isStarting,
        isFinalizing,
        isInterrupted,
        captureStatus,
        aiStatus,
        hasCaptureError: Boolean(sonioxError),
      }),
    );
  }, [
    aiStatus,
    captureStatus,
    isFinalizing,
    isInterrupted,
    isOpen,
    isStarting,
    recordingPhase,
    session,
    sonioxError,
  ]);

  const updateContextSessionId = useCallback((nextSessionId: string | null) => {
    setSession((previous) => {
      if (!previous) return previous;
      if ((previous.transcriptionId ?? null) === nextSessionId) return previous;
      return { ...previous, transcriptionId: nextSessionId };
    });
  }, [setSession]);

  const resetLocalState = useCallback(() => {
    stopAndReleaseRecordingStream();
    if (analyzingPollRef.current) {
      window.clearInterval(analyzingPollRef.current);
      analyzingPollRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    audioArchiveRecorder.cancel();
    cancelActiveRecording();
    cleanupSoniox();
    reset();
    setEditableTranscript("");
    setSonioxError(null);
    setSelectedHistoryId(null);
    setIsCancelRecordingDialogOpen(false);
    setCaptureStatus(null);
    setAiAttempts(0);
    setIsReconnecting(false);
    setShowReconnectBanner(false);
    setFinalizeFailed(false);
    hasAutoAppliedRef.current = false;
    shouldAutoApplyRef.current = true;
    isHydratingRef.current = false;
    hydrationKeyRef.current = null;
    autoRetriedRef.current = false;
    finalizingRef.current = false;
    pendingAiExtractionRef.current = false;
    reconnectAttemptRef.current = 0;
    updateContextSessionId(null);
    resetSession();
  }, [
    audioArchiveRecorder,
    cancelActiveRecording,
    stopAndReleaseRecordingStream,
    cleanupSoniox,
    reset,
    resetSession,
    setEditableTranscript,
    setSonioxError,
    updateContextSessionId,
  ]);

  const clearSession = useCallback(() => {
    discardedSessionIdRef.current = sessionIdRef.current;
    discardedPetIdRef.current = petId || discardedPetIdRef.current;
    resetLocalState();
    setMachineState(isOpen && session ? "draft" : "closed");
  }, [isOpen, petId, resetLocalState, session, sessionIdRef]);

  const discardSession = useCallback(() => {
    discardedSessionIdRef.current = sessionIdRef.current;
    discardedPetIdRef.current = petId || discardedPetIdRef.current;
    resetLocalState();
    setSession(null);
    setIsMinimized(false);
    setIsOpen(false);
    setMachineState("closed");
  }, [petId, resetLocalState, sessionIdRef, setIsMinimized, setIsOpen, setSession]);

  const closeDialog = useCallback(() => {
    if (machineState === "recording" || machineState === "paused") {
      setIsCancelRecordingDialogOpen(true);
      return;
    }
    discardSession();
  }, [discardSession, machineState]);

  const minimize = useCallback(() => {
    setIsMinimized(true);
  }, [setIsMinimized]);

  const maximize = useCallback(() => {
    setIsMinimized(false);
    setIsOpen(true);
  }, [setIsMinimized, setIsOpen]);

  const open = useCallback((config: TranscriptionSessionConfig) => {
    setSession(config);
    setIsMinimized(false);
    setIsOpen(true);
    setMachineState("draft");
  }, [setIsMinimized, setIsOpen, setSession]);

  const refreshHistory = useCallback(() => {
    if (!petId) return;
    void fetchHistory();
  }, [fetchHistory, petId]);

  const hydrateSession = useCallback(async () => {
    if (!isOpen || !session || !petId) return;

    const hydrationKey = `${petId}:${initialTranscriptionId ?? "latest"}`;
    if (hydrationKeyRef.current === hydrationKey) return;
    hydrationKeyRef.current = hydrationKey;

    void fetchHistory();

    if (disableAutoRecovery) {
      return;
    }

    isHydratingRef.current = true;
    try {
      const loaded = await loadLatestSession({
        blockedPetId: discardedPetIdRef.current,
        blockedSessionId: discardedSessionIdRef.current,
      });

      if (!loaded) return;

      updateContextSessionId(loaded.id);

      // Reconcilia com o rascunho local: se ele estiver na frente do banco
      // (queda de rede/crash antes do proximo sync), ele vence.
      const localDraft = await loadTranscriptDraft(loaded.id);
      const { transcript: recoveredTranscript } = chooseTranscriptSource(
        loaded.transcript ?? "",
        localDraft?.transcript ?? null,
      );

      setEditableTranscript(recoveredTranscript);
      setCaptureStatus(isCaptureStatus(loaded.status) ? loaded.status : "draft");
      setAiAttempts(loaded.ai_attempts ?? 0);
      loadFromSession({
        transcript: recoveredTranscript,
        structured: asStructuredRecord(loaded.structured),
        summary: loaded.summary,
        diagnosis_suggestions: loaded.diagnosis_suggestions,
        unknown_terms: loaded.unknown_terms,
        detected_language: loaded.detected_language,
        ai_status: loaded.ai_status ?? "idle",
        ai_last_error: loaded.ai_last_error,
      });
      setSelectedHistoryId(loaded.id);
    } finally {
      window.setTimeout(() => {
        isHydratingRef.current = false;
      }, 100);
    }
  }, [
    disableAutoRecovery,
    fetchHistory,
    initialTranscriptionId,
    isOpen,
    loadFromSession,
    loadLatestSession,
    petId,
    session,
    setEditableTranscript,
    updateContextSessionId,
  ]);

  useEffect(() => {
    if (!isOpen || !session) {
      hydrationKeyRef.current = null;
      return;
    }
    void hydrateSession();
  }, [hydrateSession, isOpen, session]);

  useEffect(() => {
    setSelectedHistoryId(null);
  }, [petId]);

  // Poll enquanto a IA processa: fonte unica da verdade e `ai_status` do banco,
  // nunca inferido a partir de estado local.
  useEffect(() => {
    if (machineState !== "analyzing" || !isOpen || !sessionId) {
      if (analyzingPollRef.current) {
        window.clearInterval(analyzingPollRef.current);
        analyzingPollRef.current = null;
      }
      return;
    }

    if (analyzingPollRef.current) {
      window.clearInterval(analyzingPollRef.current);
    }

    analyzingPollRef.current = window.setInterval(() => {
      void fetchSessionById(sessionId).then((currentSession) => {
        if (!currentSession) return;

        setCaptureStatus(isCaptureStatus(currentSession.status) ? currentSession.status : null);
        setAiAttempts(currentSession.ai_attempts ?? 0);

        // Linha abandonada em "processing": ninguem vai conclui-la sozinha, e o steal
        // do servidor so acontece quando chega uma requisicao nova. Sem isto a UI
        // ficaria em "Gerando estrutura da consulta..." indefinidamente.
        if (
          currentSession.ai_status === "processing" &&
          isAiProcessingStale(currentSession.ai_started_at, Date.now())
        ) {
          console.warn("[transcription] analyzing:staleProcessingDetected", {
            transcriptionId: sessionId,
            aiStartedAt: currentSession.ai_started_at,
          });
          markAiFailed();
          return;
        }

        loadFromSession({
          transcript: currentSession.transcript ?? "",
          structured: asStructuredRecord(currentSession.structured),
          summary: currentSession.summary,
          diagnosis_suggestions: currentSession.diagnosis_suggestions ?? [],
          unknown_terms: currentSession.unknown_terms ?? [],
          detected_language: currentSession.detected_language,
          ai_status: currentSession.ai_status ?? "idle",
          ai_last_error: currentSession.ai_last_error,
        });
      });
    }, 5000);

    return () => {
      if (analyzingPollRef.current) {
        window.clearInterval(analyzingPollRef.current);
        analyzingPollRef.current = null;
      }
    };
  }, [fetchSessionById, isOpen, loadFromSession, machineState, markAiFailed, sessionId]);

  /**
   * Teto absoluto para o estado "analisando", independente do que o banco diga.
   * Cobre o caso em que a linha nunca chegou a ser reclamada (`ai_started_at` nulo)
   * porque o invoke nao alcancou o servidor — ai a checagem de staleness do poll,
   * que depende do carimbo, nunca dispararia.
   */
  useEffect(() => {
    if (machineState !== "analyzing") return;

    const timeoutId = window.setTimeout(() => {
      console.warn("[transcription] analyzing:watchdogFired", { transcriptionId: sessionId });
      markAiFailed();
    }, AI_ANALYZING_STALE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [machineState, markAiFailed, sessionId]);

  useEffect(() => {
    if (machineState !== "ready" || !sessionId) return;
    if (isHydratingRef.current) return;
    void fetchHistory();
  }, [fetchHistory, machineState, sessionId]);

  useEffect(() => {
    if (
      machineState === "ready" &&
      editableTranscript &&
      onApply &&
      shouldAutoApplyRef.current &&
      !hasAutoAppliedRef.current
    ) {
      hasAutoAppliedRef.current = true;
      onApply(structured ?? {}, editableTranscript, summary ?? null);
    }
  }, [editableTranscript, machineState, onApply, structured, summary]);

  /** Retenta apenas a gravacao final do transcript, quando ela falhou por falta de rede. */
  const retryFinalize = useCallback(async () => {
    await handleCaptureFinalized(editableTranscript);
  }, [editableTranscript, handleCaptureFinalized]);

  const retryAiExtraction = useCallback(async () => {
    const transcriptionId = sessionIdRef.current;
    if (!transcriptionId) return;

    if (typeof navigator !== "undefined" && !navigator.onLine) {
      // Deixa pendente em vez de queimar uma tentativa fadada a falhar.
      pendingAiExtractionRef.current = true;
      markAiFailed();
      return;
    }

    pendingAiExtractionRef.current = false;
    await runAiExtraction(transcriptionId, crypto.randomUUID(), { metadata: metadataRef.current });
  }, [markAiFailed, runAiExtraction, sessionIdRef]);

  /**
   * Extracao adiada por falta de rede: roda sozinha assim que a conexao voltar.
   * O listener fica sempre registrado e a checagem acontece dentro dele — condicionar
   * o efeito ao ref o tornaria dependente de um render cair na hora certa.
   */
  useEffect(() => {
    const handleOnline = () => {
      if (!pendingAiExtractionRef.current) return;
      console.log("[transcription] aiExtraction:resumingAfterOnline");
      void retryAiExtraction();
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [retryAiExtraction]);

  // Uma unica retentativa automatica apos a primeira falha da IA. Depois disso,
  // so manual (via retryAiExtraction, exposto na UI).
  useEffect(() => {
    if (machineState !== "analysis_failed" || autoRetriedRef.current) return;

    const { shouldRetryAutomatically, delayMs } = decideAiRetry(aiAttempts);
    if (!shouldRetryAutomatically) return;

    autoRetriedRef.current = true;
    const timeoutId = window.setTimeout(() => {
      void retryAiExtraction();
    }, delayMs);

    return () => window.clearTimeout(timeoutId);
  }, [aiAttempts, machineState, retryAiExtraction]);

  const handleStartRecordingWithArchive = useCallback(async () => {
    const shouldStartFreshSession =
      recordingPhase === "idle" &&
      (machineState === "ready" || machineState === "analysis_failed");

    if (shouldStartFreshSession) {
      discardedSessionIdRef.current = sessionIdRef.current;
      discardedPetIdRef.current = petId || discardedPetIdRef.current;
      resetLocalState();
      setMachineState("draft");
    }

    hasAutoAppliedRef.current = false;
    shouldAutoApplyRef.current = true;
    finalizingRef.current = false;
    pendingAiExtractionRef.current = false;

    let sharedStream: MediaStream | null = null;
    try {
      sharedStream = await navigator.mediaDevices.getUserMedia({
        audio: DEFAULT_AUDIO_CONSTRAINTS,
      });
    } catch (captureError) {
      setSonioxError(getMicrophoneErrorMessage(captureError, t));
      return false;
    }

    const started = await handleStartRecording(sharedStream);
    if (!started) {
      sharedStream.getTracks().forEach((track) => track.stop());
      audioArchiveRecorder.cancel();
      return false;
    }

    if (sessionIdRef.current) {
      updateContextSessionId(sessionIdRef.current);
    }

    micStreamRef.current = sharedStream;

    if (isAudioArchiveEnabled) {
      void audioArchiveRecorder.start({
        consultationTranscriptionId: sessionIdRef.current,
        stream: sharedStream.clone(),
      });
    }

    return true;
  }, [
    audioArchiveRecorder,
    handleStartRecording,
    isAudioArchiveEnabled,
    machineState,
    petId,
    recordingPhase,
    resetLocalState,
    sessionIdRef,
    setMachineState,
    setSonioxError,
    t,
    updateContextSessionId,
  ]);

  const handlePauseRecordingWithArchive = useCallback(() => {
    if (isAudioArchiveEnabled) {
      audioArchiveRecorder.pause();
    }
    handlePauseRecording();
    stopAndReleaseRecordingStream();
  }, [audioArchiveRecorder, handlePauseRecording, isAudioArchiveEnabled, stopAndReleaseRecordingStream]);

  const handleResumeRecordingWithArchive = useCallback(async (): Promise<boolean> => {
    let sharedStream: MediaStream | null = null;
    if (isAudioArchiveEnabled) {
      sharedStream = audioArchiveRecorder.cloneStream();
    }

    if (!sharedStream) {
      try {
        sharedStream = await navigator.mediaDevices.getUserMedia({
          audio: DEFAULT_AUDIO_CONSTRAINTS,
        });
      } catch (captureError) {
        setSonioxError(getMicrophoneErrorMessage(captureError, t));
        return false;
      }
    }

    const resumed = await handleResumeRecording(sharedStream);
    if (!resumed) {
      sharedStream.getTracks().forEach((track) => track.stop());
      audioArchiveRecorder.cancel();
      return false;
    }

    micStreamRef.current = sharedStream;

    if (isAudioArchiveEnabled) {
      audioArchiveRecorder.resume();
    }

    return true;
  }, [
    audioArchiveRecorder,
    handleResumeRecording,
    isAudioArchiveEnabled,
    setSonioxError,
    t,
  ]);

  // Reconexao automatica apos queda: 2 tentativas em silencio, depois banner
  // visivel, e continua tentando indefinidamente em segundo plano. O botao
  // manual (`reconnect`) forca uma tentativa imediata a qualquer momento.
  const attemptReconnect = useCallback(async () => {
    // Sem rede, tentar so gastaria getUserMedia e handshake fadados a falhar.
    // O listener de `online` retoma assim que a conexao voltar.
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      setShowReconnectBanner(true);
      return;
    }

    setIsReconnecting(true);
    const succeeded = await handleResumeRecordingWithArchive();
    setIsReconnecting(false);

    if (succeeded) {
      reconnectAttemptRef.current = 0;
      setShowReconnectBanner(false);
      setIsInterrupted(false);
      return;
    }

    const attempt = reconnectAttemptRef.current;
    const decision = decideReconnect(attempt);
    reconnectAttemptRef.current = attempt + 1;
    if (decision.showBanner) setShowReconnectBanner(true);

    if (reconnectAttemptRef.current >= MAX_AUTO_RECONNECT_ATTEMPTS) {
      // Para de tentar sozinho: evita loop infinito contra um erro permanente
      // (ex.: config quebrada). O botao "Reconectar" no banner continua disponivel.
      return;
    }

    reconnectTimeoutRef.current = window.setTimeout(() => {
      void attemptReconnect();
    }, decision.delayMs);
  }, [handleResumeRecordingWithArchive, setIsInterrupted]);

  useEffect(() => {
    if (!isInterrupted) {
      reconnectAttemptRef.current = 0;
      setShowReconnectBanner(false);
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      return;
    }

    const decision = decideReconnect(reconnectAttemptRef.current);
    reconnectTimeoutRef.current = window.setTimeout(() => {
      void attemptReconnect();
    }, decision.delayMs);

    // Internet de volta: zera o backoff e tenta na hora, em vez de esperar o
    // proximo intervalo (ou nunca, se o teto de tentativas ja tiver sido atingido).
    const handleOnline = () => {
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      reconnectAttemptRef.current = 0;
      void attemptReconnect();
    };
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInterrupted]);

  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptRef.current = 0;
    void attemptReconnect();
  }, [attemptReconnect]);

  const handleStopRecordingWithArchive = useCallback(async () => {
    if (isAudioArchiveEnabled) {
      audioArchiveRecorder.stopAndUpload();
    }
    handleStopRecording();
    stopAndReleaseRecordingStream();

    const transcriptionId = sessionIdRef.current;

    if (!transcriptionId || !editableTranscript) return;

    void captureTranscriptionInfo({
      consultationId: transcriptionId,
      petId,
      petName,
      ownerName: pet?.owner?.name ?? null,
      transcriptionText: editableTranscript,
    });
  }, [
    audioArchiveRecorder,
    editableTranscript,
    isAudioArchiveEnabled,
    handleStopRecording,
    pet?.owner?.name,
    petId,
    petName,
    sessionIdRef,
    stopAndReleaseRecordingStream,
  ]);

  const applyHistoryEntry = useCallback((entry: ConsultationTranscriptionRow) => {
    // Resolvido antes do `reset()` abaixo, que zera o estado vivo do painel.
    const resolved = resolveHistoryApplySource({
      entry: {
        id: entry.id,
        transcript: entry.transcript,
        structured: entry.structured,
        summary: entry.summary,
      },
      live: {
        selectedHistoryId,
        transcript: editableTranscript,
        structured,
        summary,
        aiStatus,
      },
    });

    audioArchiveRecorder.cancel();
    cancelActiveRecording();
    cleanupSoniox();
    reset();
    setEditableTranscript(resolved.transcript);
    setSonioxError(null);
    setCaptureStatus(isCaptureStatus(entry.status) ? entry.status : "finalized");
    setAiAttempts(entry.ai_attempts ?? 0);
    hasAutoAppliedRef.current = false;
    shouldAutoApplyRef.current = false;
    setSelectedHistoryId(entry.id);
    updateContextSessionId(entry.id);

    loadFromSession({
      transcript: resolved.transcript,
      structured: resolved.structured,
      summary: resolved.summary,
      diagnosis_suggestions: entry.diagnosis_suggestions || [],
      unknown_terms: entry.unknown_terms || [],
      detected_language: entry.detected_language,
      ai_status: entry.ai_status ?? "idle",
      ai_last_error: entry.ai_last_error,
    });

    const structuredData = resolved.structured as Partial<MedicalRecordInput>;
    const canApply = Boolean(onApply) && Boolean(resolved.transcript.trim());
    if (canApply) {
      onApply?.(structuredData, resolved.transcript, resolved.summary);
    } else {
      onMergeFields?.(structuredData);
    }
  }, [
    aiStatus,
    audioArchiveRecorder,
    cancelActiveRecording,
    cleanupSoniox,
    editableTranscript,
    loadFromSession,
    onApply,
    onMergeFields,
    reset,
    selectedHistoryId,
    setEditableTranscript,
    setSonioxError,
    structured,
    summary,
    updateContextSessionId,
  ]);

  const reviewHistoryEntry = useCallback((entry: ConsultationTranscriptionRow) => {
    hasAutoAppliedRef.current = false;
    shouldAutoApplyRef.current = false;
    setEditableTranscript(entry.transcript ?? "");
    setSelectedHistoryId(entry.id);
    setSonioxError(null);
    setCaptureStatus(isCaptureStatus(entry.status) ? entry.status : "finalized");
    setAiAttempts(entry.ai_attempts ?? 0);

    loadFromSession({
      transcript: entry.transcript || "",
      structured: asStructuredRecord(entry.structured),
      summary: entry.summary,
      diagnosis_suggestions: entry.diagnosis_suggestions || [],
      unknown_terms: entry.unknown_terms || [],
      detected_language: entry.detected_language,
      ai_status: entry.ai_status ?? "idle",
      ai_last_error: entry.ai_last_error,
    });

    // O item pode ter transcricao boa e campos vazios (falha da IA no encerramento).
    // Nesse caso a revisao reprocessa o proprio registro, em vez de abrir uma ficha
    // vazia. A extracao mira `entry.id` diretamente: a sessao ativa nao e trocada.
    const decision = decideHistoryReview({
      transcript: entry.transcript,
      structured: entry.structured,
      summary: entry.summary,
      aiStatus: entry.ai_status,
      captureStatus: entry.status,
    });

    if (decision.action !== "extract") return;

    void (async () => {
      try {
        await runAiExtraction(entry.id, crypto.randomUUID(), { metadata: metadataRef.current });
      } finally {
        // A linha do historico so e refetchada na hidratacao e ao voltar para
        // "ready"; sem isto o botao "Aplicar" seguiria usando os campos vazios.
        refreshHistory();
      }
    })();
  }, [loadFromSession, refreshHistory, runAiExtraction, setEditableTranscript, setSonioxError]);

  const hasStableCompletedTranscription = useMemo(() => {
    if (machineState !== "ready") return false;
    const hasStructuredFields = structured && Object.keys(structured).length > 0;
    return (
      editableTranscript.trim().length > 0 ||
      hasStructuredFields ||
      Boolean(summary) ||
      diagnosisSuggestions.length > 0 ||
      unknownTerms.length > 0
    );
  }, [diagnosisSuggestions.length, editableTranscript, machineState, structured, summary, unknownTerms.length]);

  const canClearTranscription = useMemo(() => {
    const hasStructuredFields = structured && Object.keys(structured).length > 0;
    return (
      editableTranscript.trim().length > 0 ||
      Boolean(summary) ||
      diagnosisSuggestions.length > 0 ||
      unknownTerms.length > 0 ||
      hasStructuredFields
    );
  }, [diagnosisSuggestions.length, editableTranscript, structured, summary, unknownTerms.length]);

  return {
    state: machineState,
    isOpen,
    isMinimized,
    session,
    petName,
    ownerName: pet?.owner?.name ?? null,
    clinicId,
    appointmentDate,
    summary,
    warning,
    error,
    transcript,
    structured,
    diagnosisSuggestions: diagnosisSuggestions as DiagnosisSuggestion[],
    unknownTerms: unknownTerms as UnknownTerm[],
    detectedLanguage,
    languageWarning: ensurePortugueseLabel(detectedLanguage),
    isProcessing,
    isStarting,
    isFinalizing,
    isTimerRunning: isTimerRunning(machineState),
    recordingPhase,
    recordingDuration,
    editableTranscript,
    setEditableTranscript,
    sonioxError,
    sessionError,
    isSessionLoading,
    syncStatus,
    lastSyncedAt,
    isInterrupted,
    showReconnectBanner,
    isReconnecting,
    reconnect,
    finalizeFailed,
    retryFinalize,
    history,
    isHistoryLoading,
    selectedHistoryId,
    isCancelRecordingDialogOpen,
    hasStableCompletedTranscription,
    canClearTranscription,
    open,
    minimize,
    maximize,
    closeDialog,
    clearSession,
    discardSession,
    setIsCancelRecordingDialogOpen,
    startRecording: handleStartRecordingWithArchive,
    pauseRecording: handlePauseRecordingWithArchive,
    resumeRecording: handleResumeRecordingWithArchive,
    stopRecording: handleStopRecordingWithArchive,
    retryAiExtraction,
    refreshHistory,
    applyHistoryEntry,
    reviewHistoryEntry,
    onApply,
    onMergeFields,
    sessionId,
  };
};
